import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { AddParticipantsModal } from 'components/AddParticipantsModal';
import LoadingScreen from 'components/LoadingScreen';
import { vacancyStore } from 'stores/vacancyStore';
import { chatApi } from 'api';
import { PATHS } from 'routes/paths';
import type { VacancyLevel } from 'stores/vacancyStore';
import { CandidateStatus } from 'stores/vacancyStore';
import type { Candidate } from 'stores/vacancyStore';
import { format } from 'date-fns';
import { Interview } from 'types/interview';
import { handleDownloadReport } from 'utils/downloadReport';
import VacancySkillsWidget from 'components/VacancyStats/VacancySkillsWidget';

const getDisplayName = (c: Candidate) =>
  c.user ? [c.user.firstName, c.user.lastName].filter(Boolean).join(' ') || c.email : c.email;

const LEVEL_LABELS: Record<VacancyLevel, string> = {
  JUNIOR: 'Начинающий',
  MIDDLE: 'С опытом',
  SENIOR: 'Эксперт'
};

const CANDIDATE_STATUS_LABELS: Record<CandidateStatus, string> = {
  [CandidateStatus.INVITED]: 'Приглашён',
  [CandidateStatus.IN_PROGRESS]: 'В процессе',
  [CandidateStatus.IN_PROGRESS_INTERVIEW]: 'Прохождение интервью',
  [CandidateStatus.IN_PROGRESS_EXTRACT]: 'Определение навыков',
  [CandidateStatus.COMPLETED]: 'Завершён',
  [CandidateStatus.CANCELLED]: 'Пропущен'
};

const CANDIDATE_STATUS_ICONS: Record<CandidateStatus, string> = {
  [CandidateStatus.INVITED]: 'mdi:calendar-check',
  [CandidateStatus.IN_PROGRESS]: 'mdi:clock-outline',
  [CandidateStatus.IN_PROGRESS_INTERVIEW]: 'mdi:clock-outline',
  [CandidateStatus.IN_PROGRESS_EXTRACT]: 'mdi:clock-outline',
  [CandidateStatus.COMPLETED]: 'mdi:check-circle',
  [CandidateStatus.CANCELLED]: 'mdi:cancel'
};

const getGradeColor = (grade: number): 'success' | 'secondary' | 'warning' | 'error' => {
  if (grade >= 9) return 'success';
  if (grade >= 7) return 'secondary';
  if (grade >= 4) return 'warning';
  return 'error';
};

const Overline = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 1
    }}
  >
    {children}
  </Typography>
);

const surfaceSx = {
  p: 3,
  borderRadius: 3,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'grey.300'
} as const;

const getStatusBg = (status: CandidateStatus) => (theme: any) => {
  if (
    status === CandidateStatus.IN_PROGRESS_INTERVIEW ||
    status === CandidateStatus.IN_PROGRESS_EXTRACT
  )
    return alpha(theme.palette.primary.main, 0.12);
  if (status === CandidateStatus.COMPLETED) return alpha(theme.palette.success.main, 0.16);
  if (status === CandidateStatus.INVITED) return alpha(theme.palette.info.main, 0.16);
  return alpha(theme.palette.error.main, 0.16);
};

const getStatusColor = (status: CandidateStatus) => {
  if (
    status === CandidateStatus.IN_PROGRESS_INTERVIEW ||
    status === CandidateStatus.IN_PROGRESS_EXTRACT
  )
    return 'primary.dark';
  if (status === CandidateStatus.COMPLETED) return 'success.dark';
  if (status === CandidateStatus.INVITED) return 'info.dark';
  return 'error.dark';
};

const VacancyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { vacancy } = vacancyStore;

  const handleBack = () => {
    navigate(PATHS.hr.company);
  };

  const [addParticipantsOpen, setAddParticipantsOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; candidateId: string } | null>(
    null
  );
  const [messageModal, setMessageModal] = useState<{ candidateId: string; userId: string } | null>(
    null
  );
  const [messageText, setMessageText] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [inviteLinkModal, setInviteLinkModal] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const TEMPLATE_MESSAGE = `Благодарим за прохождение интервью!\nБудем рады обсудить некоторые моменты подробнее и лучше понять, насколько наши ожидания и задачи совпадают.`;

  const handleOpenMessageModal = useCallback((candidateId: string, userId: string) => {
    setMessageText(TEMPLATE_MESSAGE);
    setMessageModal({ candidateId, userId });
  }, [TEMPLATE_MESSAGE]);

  const handleSendMessage = async () => {
    if (!messageModal?.userId || !messageText.trim()) return;
    setMessageSending(true);
    try {
      const conversation = await chatApi.findOrCreateConversation(messageModal.userId);
      await chatApi.sendMessage(conversation.id, messageText.trim());
      setMessageModal(null);
    } finally {
      setMessageSending(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteLinkModal) return;
    try {
      await navigator.clipboard.writeText(inviteLinkModal);
      setSnackbar({
        open: true,
        message: 'Ссылка успешно скопирована в буфер обмена',
        severity: 'success'
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Не удалось копировать ссылку для приглашения',
        severity: 'error'
      });
    }
  };

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>, candidateId: string) => {
    e.stopPropagation();
    setMenuAnchor({ el: e.currentTarget, candidateId });
  }, []);

  const handleMenuClose = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setMenuAnchor(null);
  }, []);

  const handleAddParticipants = () => {
    setAddParticipantsOpen(true);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      vacancyStore.getVacancyById(id),
      vacancyStore.getCandidates(id),
      vacancyStore.getInterviewsByVacancyId(id)
    ]).finally(() => setLoading(false));
  }, [id]);

  const mapInterviewByUserId = useMemo(() => {
    return vacancyStore.interviews
      .filter((i) => i.finish)
      .reduce((acc, interview) => {
        const candiate = vacancyStore.candidates.find(i => i.userId === interview.userId)
        acc[candiate?.id as string] = interview;
        return acc;
      }, {} as Record<string, Interview>);
  }, [vacancyStore.interviews, vacancyStore.candidates]);

  const getUserScore = useCallback(
    (candidateId: string) => {
      return mapInterviewByUserId[candidateId]?.result?.final_result?.score;
    },
    [mapInterviewByUserId]
  );

  const infoItems = useMemo(() => {
    if (!vacancy) return [];
    const level = vacancy.specialityLevel as VacancyLevel;
    return [
      {
        label: 'Сфера деятельности',
        value: vacancy.speciality,
        icon: 'mdi:briefcase-outline',
        iconColor: 'primary.main'
      },
      {
        label: 'Участников',
        value: vacancyStore.candidates.length,
        icon: 'mdi:account-group-outline',
        iconColor: 'secondary.main'
      },
      {
        label: 'Дата создания',
        value: format(new Date(vacancy.createdAt), 'dd.MM.yyyy HH:mm'),
        icon: 'mdi:calendar-outline',
        iconColor: 'text.secondary'
      },
      {
        label: 'Уровень',
        value: LEVEL_LABELS[level] ?? vacancy.specialityLevel,
        icon: 'mdi:chart-timeline',
        iconColor: 'info.main'
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vacancy?.speciality,
    vacancy?.specialityLevel,
    vacancy?.createdAt,
    vacancyStore.candidates.length
  ]);

  const activeCandidate = useMemo(
    () =>
      menuAnchor
        ? vacancyStore.candidates.find((c) => c.id === menuAnchor.candidateId) ?? null
        : null,
    [menuAnchor, vacancyStore.candidates]
  );

  const menuItems = useMemo(() => {
    if (!activeCandidate || !vacancy) return [];

    const items: Array<{
      key: string;
      icon: string;
      label: string;
      onClick: () => void | Promise<void>;
    }> = [];

    const score = mapInterviewByUserId[activeCandidate.id]?.result?.final_result?.score;
    const candidateUserId = activeCandidate.userId;

    if (score != null) {
      items.push({
        key: 'download',
        icon: 'mdi:download',
        label: 'Скачать результат',
        onClick: () => {
          const report = mapInterviewByUserId[activeCandidate.id]?.report
          if (report) {
            handleDownloadReport(report.url)
          }
          handleMenuClose();
        }
      });
    }

    items.push({
      key: 'message',
      icon: 'mdi:message-outline',
      label: 'Написать сообщение',
      onClick: () => {
        if (candidateUserId) {
          handleOpenMessageModal(activeCandidate.id, candidateUserId);
        }
        handleMenuClose();
      }
    });

    items.push({
      key: 'resend',
      icon: 'mdi:email-sync-outline',
      label: 'Выслать приглашение повторно',
      onClick: async () => {
        try {
          await vacancyStore.resendInvitation(vacancy.id, activeCandidate.email);
          setSnackbar({
            open: true,
            message: 'Приглашение отправлено повторно',
            severity: 'success'
          });
        } catch {
          setSnackbar({
            open: true,
            message: 'Не удалось отправить приглашение',
            severity: 'error'
          });
        }
        handleMenuClose();
      }
    });

    items.push({
      key: 'resend',
      icon: 'mdi:content-copy',
      label: 'Копировать ссылку для приглашения',
      onClick: async () => {
        try {
          const res = await vacancyStore.fetchInviteLink(vacancy.id, activeCandidate.email);
          setInviteLinkModal(res.inviteLink);
        } catch {
          setSnackbar({
            open: true,
            message: 'Не удалось получить ссылку для приглашения',
            severity: 'error'
          });
        }
        handleMenuClose();
      }
    });

    items.push({
      key: 'favorite',
      icon: activeCandidate.isFavorite ? 'mdi:star' : 'mdi:star-outline',
      label: activeCandidate.isFavorite ? 'Убрать из избранных' : 'Сделать избранным',
      onClick: () => {
        if (activeCandidate.isFavorite) {
          vacancyStore.removeFromFavorites(vacancy.id, activeCandidate.id);
        } else {
          vacancyStore.addToFavorites(vacancy.id, activeCandidate.id);
        }
        handleMenuClose();
      }
    });

    return items;
  }, [activeCandidate, vacancy, mapInterviewByUserId, handleOpenMessageModal, handleMenuClose]);

  const filteredCandidates = useMemo(() => {
    const query = candidateSearch.trim().toLowerCase();
    const list = query
      ? vacancyStore.candidates.filter((c) => getDisplayName(c).toLowerCase().includes(query))
      : [...vacancyStore.candidates];
    return list.sort((a, b) => {
      const aHasScore =
        a.userId && mapInterviewByUserId[a.id]?.result?.final_result?.score != null ? 1 : 0;
      const bHasScore =
        b.userId && mapInterviewByUserId[b.id]?.result?.final_result?.score != null ? 1 : 0;
      return bHasScore - aHasScore;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateSearch, vacancyStore.candidates, mapInterviewByUserId]);

  if (!id) {
    return (
      <Stack spacing={2}>
        <Typography color="text.secondary">Вакансия не указана.</Typography>
        <Button
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={handleBack}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          К компании
        </Button>
      </Stack>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!vacancy) {
    return (
      <Stack spacing={2}>
        <Typography color="text.secondary">Вакансия не найдена.</Typography>
        <Button
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={handleBack}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          К компании
        </Button>
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: '100%', overflow: 'auto', px: 8, mx: -6, pb: 0 }} spacing={4}>
      <Box sx={{ ...surfaceSx, mt: 2 }}>
        <Overline>Вакансия</Overline>
        <Typography variant="h5" fontWeight={700} sx={{ color: 'grey.900', mb: 3 }}>
          {vacancy.position}
        </Typography>
        <Grid container spacing={2}>
          {infoItems.map(({ label, value, icon, iconColor }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                <Box
                  sx={(theme) => ({
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: iconColor,
                    bgcolor: alpha(
                      (() => {
                        const parts = iconColor.split('.');
                        const root = theme.palette[parts[0] as keyof typeof theme.palette] as any;
                        return (root?.[parts[1]] as string) || theme.palette.primary.main;
                      })(),
                      0.12
                    )
                  })}
                >
                  <Icon icon={icon} width={20} height={20} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      mb: 0.25
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                    {value}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <VacancySkillsWidget />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignSelf: 'flex-start' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Icon icon="mdi:account-plus" width={22} height={22} />}
          onClick={handleAddParticipants}
          aria-label="Добавить новых участников"
          sx={{
            py: 1.5,
            px: 3,
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1,
            boxShadow: 'none'
          }}
        >
          Добавить новых участников
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<Icon icon="mdi:chart-box-outline" width={22} height={22} />}
          onClick={() => navigate(PATHS.hr.vacancyStats.replace(':vacancyId', vacancy.id))}
          aria-label="Статистика по вакансии"
          sx={{ py: 1.5, px: 3, fontWeight: 700, textTransform: 'none', borderRadius: 1 }}
        >
          Статистика по вакансии
        </Button>
      </Stack>

      <Box sx={surfaceSx}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Overline>Участники</Overline>
          </Box>
          <TextField
            size="small"
            placeholder="Поиск по имени или email..."
            value={candidateSearch}
            onChange={(e) => setCandidateSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="mdi:magnify" width={20} height={20} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 300 }}
            aria-label="Поиск кандидатов по имени"
          />
        </Stack>

        <Grid container spacing={2}>
          {filteredCandidates.map((candidate) => {
            const displayName = getDisplayName(candidate);
            const addedAt = candidate.createdAt
              ? format(new Date(candidate.createdAt), 'dd.MM.yyyy')
              : null;
            const hasScore = candidate.userId && getUserScore(candidate.id) != null;
            const interview =
              hasScore && candidate.userId ? mapInterviewByUserId[candidate.id] : null;
            const handleCardClick = () => {
              if (interview?.id) {
                vacancyStore.candidateInterview = interview || null;
                navigate(
                  PATHS.hr.candidateResult
                    .replace(':vacancyId', vacancy.id)
                    .replace(':userId', candidate.userId)
                );
              }
            };
            return (
              <Grid item xs={12} sm={6} key={candidate.id}>
                <Box
                  role={hasScore ? 'button' : undefined}
                  tabIndex={hasScore ? 0 : undefined}
                  onClick={hasScore ? handleCardClick : undefined}
                  onKeyDown={(e) => {
                    if (hasScore && (e.key === 'Enter' || e.key === ' ') && interview?.id) {
                      e.preventDefault();
                      navigate(PATHS.results.replace(':id', interview.id));
                    }
                  }}
                  aria-label={hasScore ? `Перейти к результатам: ${displayName}` : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: candidate.isFavorite
                      ? (t) => alpha(t.palette.warning.main, 0.06)
                      : 'background.paper',
                    border: '1px solid',
                    borderColor: candidate.isFavorite
                      ? (t) => alpha(t.palette.warning.main, 0.4)
                      : 'grey.300',
                    transition: 'border-color 160ms, background-color 160ms',
                    ...(hasScore && {
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: (t) =>
                          candidate.isFavorite
                            ? alpha(t.palette.warning.main, 0.1)
                            : alpha(t.palette.primary.main, 0.04)
                      }
                    })
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      {candidate.isFavorite && (
                        <Icon
                          icon="mdi:star"
                          width={16}
                          height={16}
                          color="#F4A918"
                          style={{ flexShrink: 0 }}
                        />
                      )}
                      <Typography variant="body2" fontWeight={700} noWrap title={displayName}>
                        {displayName}
                      </Typography>
                      <Chip
                        icon={<Icon icon={CANDIDATE_STATUS_ICONS[candidate.status]} />}
                        label={CANDIDATE_STATUS_LABELS[candidate.status]}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: 1,
                          bgcolor: getStatusBg(candidate.status),
                          color: getStatusColor(candidate.status),
                          '& .MuiChip-icon': { color: 'inherit' }
                        }}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      noWrap
                      title={candidate.email}
                    >
                      {candidate.email}
                    </Typography>
                    {addedAt && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        display="block"
                        sx={{ mt: 0.25 }}
                      >
                        Добавлен: {addedAt}
                      </Typography>
                    )}
                  </Box>
                  {hasScore ? (
                    <Chip
                      label={`${Number(getUserScore(candidate.id)).toFixed(1)} / 10`}
                      color={getGradeColor(Number(getUserScore(candidate.id)))}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        minWidth: 72,
                        ml: 2,
                        flexShrink: 0,
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Chip
                      label="Нет оценки"
                      size="small"
                      sx={{
                        ml: 2,
                        flexShrink: 0,
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        color: 'text.secondary'
                      }}
                    />
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, candidate.id)}
                    sx={{ ml: 0.5, flexShrink: 0 }}
                  >
                    <Icon icon="mdi:dots-vertical" width={20} height={20} />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {vacancyStore.candidates.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Icon icon="mdi:account-multiple-outline" width={40} height={40} color="#919EAB" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Пока нет кандидатов. Добавьте участников кнопкой выше.
            </Typography>
          </Box>
        )}
        {vacancyStore.candidates.length > 0 && filteredCandidates.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Никого не найдено по запросу «{candidateSearch}».
          </Typography>
        )}
      </Box>

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={() => handleMenuClose()}>
        {menuItems.map(({ key, icon, label, onClick }) => (
          <MenuItem key={key} onClick={onClick} sx={{ gap: 1 }}>
            <Icon icon={icon} width={18} height={18} />
            {label}
          </MenuItem>
        ))}
      </Menu>

      <AddParticipantsModal
        open={addParticipantsOpen}
        onClose={() => setAddParticipantsOpen(false)}
        vacancyId={vacancy.id}
        onSuccess={() => {
          vacancyStore.getCandidates(vacancy.id);
        }}
      />

      <Dialog
        open={Boolean(messageModal)}
        onClose={() => setMessageModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Написать сообщение</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={5}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMessageModal(null)}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!messageText.trim() || messageSending}
            startIcon={messageSending ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
          >
            Отправить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(inviteLinkModal)}
        onClose={() => setInviteLinkModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Ссылка для приглашения</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Скопируйте ссылку и отправьте её кандидату для прохождения интервью.
          </Typography>
          <TextField
            fullWidth
            value={inviteLinkModal ?? ''}
            onFocus={(e) => e.target.select()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopyInviteLink}
                    edge="end"
                    aria-label="Копировать ссылку"
                  >
                    <Icon icon="mdi:content-copy" width={20} height={20} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInviteLinkModal(null)}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Закрыть
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleCopyInviteLink()
              setInviteLinkModal(null)
            }}
            startIcon={<Icon icon="mdi:content-copy" width={18} height={18} />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
          >
            Копировать и закрыть
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default observer(VacancyPage);
